import { Snackbar, Alert } from '@material-ui/core';
import React, { useContext, createContext, useState, Dispatch } from 'react';

interface SnackbarProps {
  message: string;
  severity?: 'success' | 'info' | 'warning' | 'error';
  open: boolean;
}

export interface SnackbarContextProps {
  setSnackbar: Dispatch<SnackbarProps>;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

export interface SnackbarProviderProps {
  children: JSX.Element;
}

export const SnackbarContext = createContext<SnackbarContextProps>({
  setSnackbar: undefined,
  showError: undefined,
  showSuccess: undefined,
});

/**
 * Hook for obtaining the snackbar function from the context
 */

export const useSnackbar = (): SnackbarContextProps => {
  const context = useContext(SnackbarContext);

  if (context === undefined) {
    throw new Error('SnackbarContext must be used within a SnackbarProvider component');
  }
  return context;
};

const initialSnackbarState: SnackbarProps = {
  message: '',
  severity: undefined,
  open: false,
};

export const SnackbarProvider = ({ children }: SnackbarProviderProps): JSX.Element => {
  const [snackbar, setSnackbar] = useState<SnackbarProps>(initialSnackbarState);

  const handleCloseSnackbar = () => setSnackbar(initialSnackbarState);

  const SnackbarUI = (
    <Snackbar
      open={snackbar.open}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={handleCloseSnackbar}
      data-testid={`${snackbar.severity}-snackbar`}
    >
      <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  return (
    <SnackbarContext.Provider
      value={{
        setSnackbar,
        showError: (message) => setSnackbar({ open: true, severity: 'error', message }),
        showSuccess: (message) => setSnackbar({ open: true, severity: 'success', message }),
      }}
    >
      {snackbar.open && SnackbarUI}
      {children}
    </SnackbarContext.Provider>
  );
};
